import { ApplicationConfig, importProvidersFrom, isDevMode } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { measureReducer } from './pages/measure/store/measure.reducer';
import { measureFeatureKey } from './pages/measure/store/measure.state';
import { MeasureEffects } from './pages/measure/store/effects/measure.effects';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SpinnerInterceptorFn } from './shared/interceptors/spinner.interceptor';
import { ChoicesEffects } from './pages/measure/store/effects/choices.effects';
import { QuestionEffects } from './pages/measure/store/effects/question.effects';
import { LikertEffects } from './pages/measure/store/effects/likert.effects';
import { ErrorInterceptorFn } from './shared/interceptors/error.interceptor';
import { FlowEffects } from './pages/measure/store/effects/flow.effects';
import { MessageService } from 'primeng/api';
import { DisplayLogicEffects } from './pages/measure/store/effects/display-logic.effects';
import { UploadInterceptorFn } from './shared/interceptors/upload.interceptor';
import { AuthInterceptorFn } from './shared/interceptors/auth.interceptor';
import { indReportsFeatureKey } from './pages/individual-reports-page/store/indTemplate.state';
import { indTemplateReducer } from './pages/individual-reports-page/store/indTemplate.reducer';
import { IndTemplateEffects } from './pages/individual-reports-page/store/indTemplate.effects';
import { indTemplateBuilderFeatureKey } from './pages/individual-reports-page/store/indTemplateBuilder.state';
import { indTemplateBuilderReducer } from './pages/individual-reports-page/store/indTemplateBuilder.reducer';
import { IndTemplateBuilderEffects } from './pages/individual-reports-page/store/indTemplateBuilder.effects';
import { groupReportsFeatureKey } from './pages/group-reports-page/store/groupTemplate.state';
import { groupTemplateReducer } from './pages/group-reports-page/store/groupTemplate.reducer';
import { GroupTemplateEffects } from './pages/group-reports-page/store/groupTemplate.effects';
import { calculatedReportFeatureKey } from './pages/group-reports-page/store/calculated-report.state';
import { calculatedReportReducer } from './pages/group-reports-page/store/calculated-report.reducer';
import { providePrimeNG } from 'primeng/config';
import { AlidadePreset } from './theme/alidade-primeng-preset';
import { MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { Tokens } from 'marked';
import { alidadeConfig as defaultAlidadeConfig } from '../environments/environment';
import { AlidadeConfigModel } from './core/services/models/alidade-config.model';

type AlidadeRuntimeGlobal = typeof globalThis & {
  __ALIDADE_CONFIG__?: AlidadeConfigModel;
};

const CODESPAN_URL_PATTERN = /^https?:\/\/[^\s<>"']+$/i;

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();

  // ngx-markdown copies these overrides onto marked's own renderer via `marked.use`, so they must
  // reach the default implementations through `this`/the prototype instead of capturing `renderer`.
  renderer.link = function (this: MarkedRenderer, token: Tokens.Link): string {
    return MarkedRenderer.prototype.link
      .call(this, token)
      .replace('<a href=', '<a target="_blank" rel="noopener noreferrer" href=');
  };

  // AI models sometimes wrap URLs in backticks; render those as links instead of dead <code> chips.
  renderer.codespan = function (this: MarkedRenderer, token: Tokens.Codespan): string {
    if (!CODESPAN_URL_PATTERN.test(token.text)) {
      return MarkedRenderer.prototype.codespan.call(this, token);
    }
    return this.link({
      type: 'link',
      raw: token.raw,
      href: token.text,
      title: null,
      text: token.text,
      tokens: [{ type: 'text', raw: token.text, text: token.text, escaped: false }],
    });
  };

  return {
    renderer,
    breaks: true,
    gfm: true,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    providePrimeNG({
      theme: {
        preset: AlidadePreset,
        options: {
          // The dark colour scheme is emitted under `:root.al-dark, :root .al-dark`. During the
          // screen-by-screen restyle the class sits on each restyled page's root element; once
          // every screen is done it moves to <html> and the app becomes dark-only.
          darkModeSelector: '.al-dark',
          // Pairs with the @layer order in styles.scss so Tailwind utilities beat PrimeNG styles.
          cssLayer: { name: 'primeng', order: 'tailwind-base, primeng, tailwind-utilities' },
        },
      },
    }),
    MessageService,
    importProvidersFrom(NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' })),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'disabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideAnimations(),
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    provideHttpClient(
      withInterceptors([SpinnerInterceptorFn, ErrorInterceptorFn, UploadInterceptorFn, AuthInterceptorFn]),
    ),
    {
      provide: 'ALIDADE_CONFIG',
      useFactory: (): AlidadeConfigModel =>
        (globalThis as AlidadeRuntimeGlobal).__ALIDADE_CONFIG__ ?? defaultAlidadeConfig,
    },
    provideStore({
      [measureFeatureKey]: measureReducer,
      [indReportsFeatureKey]: indTemplateReducer,
      [indTemplateBuilderFeatureKey]: indTemplateBuilderReducer,
      [groupReportsFeatureKey]: groupTemplateReducer,
      [calculatedReportFeatureKey]: calculatedReportReducer,
    }),
    provideEffects([
      MeasureEffects,
      ChoicesEffects,
      QuestionEffects,
      LikertEffects,
      FlowEffects,
      DisplayLogicEffects,
      IndTemplateEffects,
      IndTemplateBuilderEffects,
      GroupTemplateEffects,
    ]),

    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
  ],
};
